/** SEO head for the FAQ page (`/faq`) — injected at build via prerender; kept in sync on the client when navigating to/away. */

import { isProduction } from "@/config/config";
import { FAQ } from "@/content/marketing";
import { PUBLISHER_NODE, SITE_URL, WEBSITE_NODE } from "@/lib/marketing-jsonld";

export const FAQ_URL = "https://goodsender.com/faq";

export const FAQ_DOCUMENT_TITLE =
  "GoodSender FAQ: publishing your blog with AI";

export const FAQ_DESCRIPTION =
  "Answers about publishing your blog with AI, using GoodSender's MCP, CLI, and Skills, plus newsletter, consent, and pricing questions.";

const FAQ_TWITTER_DESCRIPTION =
  FAQ_DESCRIPTION;

const FAQ_OG_IMAGE = "https://goodsender.com/og-image.png";

/**
 * The ONLY page carrying FAQPage markup: the landing renders the same module
 * as visible content without duplicate structured data, so the two can never
 * disagree and the rich-result markup stays on a single URL.
 *
 * Only `mainEntity` is required for the rich result; `inLanguage`/`isPartOf`/
 * `publisher` are carried anyway so this page's node joins the same graph
 * /pricing declares rather than floating free. `about` is deliberately NOT
 * mirrored: on /pricing it exists to hang the `Offer` off the
 * SoftwareApplication node, and the FAQ makes no price claim of its own.
 */
export const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": `${FAQ_URL}#faq`,
  url: FAQ_URL,
  name: FAQ_DOCUMENT_TITLE,
  description: FAQ_DESCRIPTION,
  inLanguage: "en",
  isPartOf: WEBSITE_NODE,
  publisher: PUBLISHER_NODE,
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const faqBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "FAQ", item: FAQ_URL },
  ],
};

type HeadElement = { type: "meta" | "link"; props: Record<string, string> };

const FAQ_SEO_HEAD_ELEMENTS: HeadElement[] = [
  {
    type: "meta",
    props: { name: "description", content: FAQ_DESCRIPTION },
  },
  {
    type: "meta",
    props: {
      name: "robots",
      content: isProduction
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    },
  },
  // NOTE: meta keywords removed — ignored by modern search and adds clutter.
  {
    type: "meta",
    props: { name: "author", content: "Joy Labs Ventures" },
  },
  { type: "link", props: { rel: "canonical", href: FAQ_URL } },
  // Open Graph
  { type: "meta", props: { property: "og:type", content: "website" } },
  { type: "meta", props: { property: "og:url", content: FAQ_URL } },
  { type: "meta", props: { property: "og:title", content: FAQ_DOCUMENT_TITLE } },
  {
    type: "meta",
    props: { property: "og:description", content: FAQ_DESCRIPTION },
  },
  { type: "meta", props: { property: "og:image", content: FAQ_OG_IMAGE } },
  { type: "meta", props: { property: "og:image:width", content: "1200" } },
  { type: "meta", props: { property: "og:image:height", content: "630" } },
  { type: "meta", props: { property: "og:site_name", content: "GoodSender" } },
  { type: "meta", props: { property: "og:locale", content: "en_US" } },
  // Twitter Card
  {
    type: "meta",
    props: { name: "twitter:card", content: "summary_large_image" },
  },
  {
    type: "meta",
    props: { name: "twitter:title", content: FAQ_DOCUMENT_TITLE },
  },
  {
    type: "meta",
    props: {
      name: "twitter:description",
      content: FAQ_TWITTER_DESCRIPTION,
    },
  },
  { type: "meta", props: { name: "twitter:image", content: FAQ_OG_IMAGE } },
  // AEO / LLM discoverability
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms.txt",
      title: "LLM Summary",
    },
  },
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms-full.txt",
      title: "LLM Full Reference",
    },
  },
  {
    type: "link",
    props: {
      rel: "describedby",
      type: "application/json",
      href: "https://goodsender.com/api/openapi.json",
    },
  },
];

/**
 * Route-level `head()` for /faq — the SSR-era replacement for the
 * prerender/sync pair: TanStack Start renders these into the SSR HTML per
 * request and keeps them in sync on SPA navigation, so no client-side head
 * patching is needed (216d / #221).
 */
export function faqRouteHead() {
  return {
    meta: [
      { title: FAQ_DOCUMENT_TITLE },
      ...FAQ_SEO_HEAD_ELEMENTS.filter((e) => e.type === "meta").map((e) => e.props),
    ],
    links: FAQ_SEO_HEAD_ELEMENTS.filter((e) => e.type === "link").map((e) => e.props),
  };
}
